/** A company credential belongs to this Document, never a browser cookie or storage. One
 *  exception: a project switch hands one prefetched admission to the next Document through
 *  `utils/admissionHandoff.ts` (sessionStorage, written only by the unload of the switch's own
 *  navigation, deleted by the next Document's bootstrap, 30 s, bound to that route and source
 *  session). */
export interface TabCompanySession {
    sessionJwt: string;
    exp: number;
    /** When the JWT stops being good, in ms on this browser's clock: when the admission request
     *  was sent plus the server's `expires_in`, so a skewed clock neither renews late nor sends a
     *  dead token, and an answer read late (a slow network, a suspended tab) is not stretched. */
    deadline: number;
    organizationId: string;
    lineageId: string;
}

/** Inside this much of the deadline a request starts a background renewal. */
const RENEW_WITHIN_MS = 60_000;
/** The JWT is sent until this long before its deadline; after that a request waits on renewal. */
const SEND_UNTIL_MS = 5_000;
/** After a failed renewal, the next one starts no sooner than this. */
const RENEW_FLOOR_MS = 10_000;
/** An admission answered earlier (discovery's, or a switch's handoff) is installed only with more
 *  than this left; one closer to its deadline is asked for again. */
const PRELOAD_LIFE_MS = 60_000;

let current: TabCompanySession | null = null;
let revalidate: (() => Promise<void>) | null = null;
/** The renewal under way, its wait for the floor included: every request that needs a
 *  credential shares it. */
let pending: Promise<void> | null = null;
/** Ends `pending`'s wait for the floor: true renews at once, false drops the renewal, because a
 *  credential was installed meanwhile. */
let endWait: ((renew: boolean) => void) | null = null;
/** The admission owner kept a failed renewal off screen; another is due once the floor passes. */
let renewAgain = false;
let lastFailureAt = Number.NEGATIVE_INFINITY;
const listeners = new Set<() => void>();

/** The local deadline of an admission whose request was sent at `sentAt` (ms): its
 *  `expires_in`, or, from a server that does not send one yet, its absolute `exp`. The server
 *  counts `expires_in` from its answer, which comes after the send, so this errs early. */
export function credentialDeadline(
    admission: { exp: number; expires_in?: number },
    sentAt: number,
): number {
    return typeof admission.expires_in === "number"
        ? sentAt + admission.expires_in * 1000
        : admission.exp * 1000;
}

/** Whether a credential with this deadline may still be sent. */
export function isUsableDeadline(deadline: number, now = Date.now()): boolean {
    return now < deadline - SEND_UNTIL_MS;
}

/** Whether an admission answered earlier still has the life to be installed now. */
export function hasPreloadLife(deadline: number, now = Date.now()): boolean {
    return deadline - now > PRELOAD_LIFE_MS;
}

export function currentTabCompanySession(): TabCompanySession | null {
    return current;
}

/** The credential while a request may still carry it. */
export function usableTabCompanySession(now = Date.now()): TabCompanySession | null {
    return current && isUsableDeadline(current.deadline, now) ? current : null;
}

export function installTabCompanySession(session: TabCompanySession | null): void {
    current = session;
    lastFailureAt = Number.NEGATIVE_INFINITY;
    renewAgain = false;
    // An installed credential is what a renewal waiting out the floor was for.
    endWait?.(false);
    listeners.forEach((listener) => listener());
}

/** A 401 proves the server no longer takes this JWT, whatever the local deadline says. */
export function expireTabCompanySession(): void {
    if (current) current = { ...current, deadline: 0 };
}

export function setTenantSessionRevalidator(handler: (() => Promise<void>) | null): void {
    revalidate = handler;
}

/** Resolves true once `ms` pass, or with `endWait`'s answer if that comes first. */
function floorPassed(ms: number): Promise<boolean> {
    return new Promise((resolve) => {
        const timer = setTimeout(() => finish(true), ms);
        function finish(renew: boolean) {
            clearTimeout(timer);
            endWait = null;
            resolve(renew);
        }
        endWait = finish;
    });
}

/** The shared renewal: the one under way, or a new one that first waits out what is left of the
 *  floor after a failure. Bounded: the floor, then the admission owner's own deadline. */
function renewal(): Promise<void> {
    if (pending) return pending;
    const run = async () => {
        try {
            const wait = lastFailureAt + RENEW_FLOOR_MS - Date.now();
            if (wait > 0 && !(await floorPassed(wait))) return;
            if (!revalidate) {
                if (current) throw new Error("Company session validation is unavailable");
                return;
            }
            await revalidate();
            lastFailureAt = Number.NEGATIVE_INFINITY;
        } catch (failure) {
            lastFailureAt = Date.now();
            throw failure;
        }
    };
    const operation: Promise<void> = run().finally(() => {
        if (pending === operation) pending = null;
        if (renewAgain) {
            renewAgain = false;
            void renewal().catch(() => undefined);
        }
    });
    pending = operation;
    return operation;
}

/** Renews now: joins the renewal under way, and one waiting out the floor starts at once.
 *  Refresh does not finish until the backend has re-admitted the complete URL scope. */
export function revalidateTenantSession(): Promise<void> {
    const shared = renewal();
    endWait?.(true);
    return shared;
}

/** A renewal failed while the credential is still good, and the admission owner kept it off
 *  screen. The next one is due once the floor passes, whether or not a request asks for it, so
 *  a credential never lapses into an outage nobody is told about. */
export function deferRenewal(): void {
    lastFailureAt = Date.now();
    if (pending) renewAgain = true;
    else void renewal().catch(() => undefined);
}

/** A request this tab sent was refused with `project_route_disabled`: its link was switched off.
 *  Renewal asks company-session, which decides between the fallback to the unprefixed path and
 *  the "link turned off" screen. The refused request is never sent again, so no mutation or
 *  stream replays. Refusals share one renewal, which keeps the floor after a failure. */
export function renewForDisabledRoute(): void {
    if (current) void renewal().catch(() => undefined);
}

/** The JWT a request carries now. In the last minute a renewal starts in the background and
 *  the request still goes out with the current JWT; only once that JWT is no longer usable does
 *  the request wait, on the shared renewal, the floor after a failure included. */
export async function tabCompanySessionJwt(): Promise<string> {
    const now = Date.now();
    if (current && current.deadline - now <= RENEW_WITHIN_MS) {
        const renewing = renewal();
        if (usableTabCompanySession(now)) void renewing.catch(() => undefined);
        else await renewing;
    }
    const admitted = usableTabCompanySession();
    if (!admitted) throw new Error("Company session expired");
    return admitted.sessionJwt;
}
